<template>
    <div
        :class="[
            $style.recommandList,
            props.size === 'large' && $style.large,
            props.showBackground && $style.background,
            props.isMobile ? $style.recommandListMobile : ''
        ]"
        v-show="
            currentRecommondList.length > 0 || props.showBlank || props.emptyTip || props.location
        "
    >
        <div :class="$style.header">
            <slot name="left"></slot>
            <slot name="right"></slot>
        </div>
        <div
            v-loading="props.loading ? ecTemplateLoading || officialTemplatesLoading : false"
            pt-loading-background="transparent"
            :class="$style.listContainer"
        >
            <div
                :class="$style.list"
                v-if="!ecTemplateLoading && !officialTemplatesLoading"
                ref="cardRef"
            >
                <TemplateGalleryCard v-if="props.showGalleryCard"></TemplateGalleryCard>
                <TemplateCard
                    v-if="props.showBlank && props.engageType?.key !== 'ABTEST'"
                    :isEmpty="true"
                    :blankText="props.engageType?.blankText"
                    @click="handleCreateTemplate({ id: 'blank', type: props.engageType.key })"
                ></TemplateCard>
                <template v-for="(item, index) in currentRecommondList" :key="item?.id ?? index">
                    <div
                        :class="
                            ($style.cardBase,
                            item?.tags.layout === 'knowledge'
                                ? 'ecCard'
                                : item?.tags.layout === 'collection'
                                ? 'ecCollection'
                                : '')
                        "
                        class="cardBase"
                    >
                        <wac-component
                            v-if="item?.isfromEC"
                            :asset-id="item?.id"
                            :component-data="item?.data"
                            :displayMode="
                                item?.tags.layout === 'detail' ||
                                item?.tags.layout === 'instruction'
                                    ? 'card'
                                    : item?.tags.layout
                            "
                            :lang="wacLang"
                            @preview="handleViewTemplate"
                            @view="handleInfoTemplate"
                            @create="handleCreateTemplate"
                        ></wac-component>
                        <TemplateCard
                            v-if="!item?.isfromEC"
                            :src="item?.pcThumbnail || item?.mobileThumbnail"
                            :template="item"
                            :showLayer="props.showLayer"
                            :isMobile="isMobile"
                            @view="handleViewTemplate"
                            @create="handleCreateTemplate"
                            @edit="handeEditTemplate"
                            @delete="handleDeleteTemplate"
                        >
                        </TemplateCard>
                    </div>
                </template>

                <!-- 空白提示 -->
                <div :class="$style.emptyTip" v-if="!currentRecommondList.length && props.emptyTip">
                    <img src="@assets/images/form-no-data.png" alt="" />
                    <p :class="$style.title">{{ $t('product.template_not_found') }}</p>
                    <div :class="$style.tip">
                        <span>{{ $t('product.please_suggest') }}</span
                        ><span :class="$style.suggest" @click="openSuggestDialog">{{
                            $t('product.suggest_now')
                        }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import TemplateCard from './TemplateCard.vue';
import TemplateGalleryCard from './TemplateGalleryCard.vue';
export default {
    name: 'TemplateRecommandList',
    components: {
        TemplateCard,
        TemplateGalleryCard
    }
};
</script>

<script lang="ts" setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { cloneDeep } from 'lodash-es';
import { useECTemplates } from '@models/ecshop/asset';
import { useAllOfficialTemplates } from '@models/experience/useExperienceTemplatesV4';
import { useECEvent, ECDataLocation } from '@experience/hooks/template.hooks';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { ECTemplate } from '@models/ecshop/asset';
import type { TypeItem } from '@experience/static/config/experience.type';
import analyticsService from '@assets/services/analytics.service';
import { suggest } from '@/utils/common.util';
import { useAccountInfo } from '@models/account/useAccountInfo';
import 'intersection-observer';

// interface
interface Props {
    position: ECDataLocation;
    list?: ExperienceTemplateV4[] | ECTemplate[];
    showLayer?: boolean; // 是否显示卡片上遮罩
    showBlank?: boolean; // 是否显示空白创建
    showGalleryCard?: boolean;
    size?: 'large';
    engageType?: TypeItem;
    isFromEC?: boolean;
    location?: string; //'Blank page' | 'Create Experience popup' | 'Home page' | ‘Create Popup Dialog’
    showBackground?: boolean;
    recommandType?: 'purpose' | 'industry';
    currentTemplate?: ExperienceTemplateV4 | ECTemplate; //当前展示的模板 推荐相关时需要
    emptyTip?: boolean;
    loading?: boolean;
    isMobile?: false;
    needLoadTemplate?: boolean; //当前展示的模板 推荐相关时需要
}
// props
const props = withDefaults(defineProps<Props>(), {
    showLayer: true,
    showBlank: false,
    showGalleryCard: false,
    showBackground: true,
    isMobile: false,
    emptyTip: false,
    loading: true,
    needLoadTemplate: true
});
// emits
const emit = defineEmits(['view', 'create', 'edit', 'delete']);

const router = useRouter();
// state
const { getECTemplates, allECTemplates, ecTemplateLoading, wacLang } = useECTemplates();
// computed
const { officialTemplates, officialTemplatesLoading } = useAllOfficialTemplates();

const currentRecommondList = ref([]) as any;

const { account } = useAccountInfo();

const collectionColorList = ref([
    '#5E95FA',
    '#FE8D55',
    '#9266D6',
    '#2FCD64',
    '#A9D71A',
    '#50BEFA',
    '#EAAA71',
    '#F7C41E'
]);
const cardColorList = ref([
    {
        background: '#F5E5F4',
        font: '#70226A'
    },
    {
        background: '#E5E4F6',
        font: '#2F2A69'
    },
    {
        background: '#E1ECF8',
        font: '#2B557F'
    },
    {
        background: '#DDF0F1',
        font: '#19585E'
    },
    {
        background: '#DEF1E3',
        font: '#1C542B'
    },
    {
        background: '#E5EED6',
        font: '#354B11'
    },
    {
        background: '#F7F1DB',
        font: '#655417'
    },
    {
        background: '#F8ECDA',
        font: '#735425'
    }
]);

const cardRef = ref();

// watch
watch(
    () => props.list,
    (newVal, oldVal) => {
        if (oldVal && newVal !== oldVal) {
            currentRecommondList.value = explorList(cloneDeep(props.list));
            nextTick(() => {
                handleScroll();
            })
        }
    }
);
watch(
    () => [allECTemplates.value, officialTemplates.value],
    () => {
        if ((allECTemplates.value || officialTemplates.value) && !props.list?.length) {
            currentRecommondList.value = explorList(getDefaultRecommandList());
            nextTick(() => {
                handleScroll();
            })
        }
    },
    {
        immediate: true
    }
);
// onMounted
onMounted(() => {
    if (!allECTemplates.value && props.needLoadTemplate) {
        getECTemplates();
    }
});

// function
function getDefaultRecommandList(currenTemplate?: ExperienceTemplateV4 | ECTemplate) {
    if (!officialTemplates.value && !allECTemplates.value) return [];
    let list = props.isFromEC
        ? allECTemplates.value
        : props.engageType && props.engageType?.key !== 'POPUP'
        ? officialTemplates.value
        : cloneDeep(officialTemplates.value || []).concat(allECTemplates.value || []);
    return props.location
        ? cloneDeep(list).filter((item: ExperienceTemplateV4 | ECTemplate) => {
              return item.tags?.location === props.location;
          })
        : props.recommandType
        ? getRandomRecommandList(
              cloneDeep(list),
              currenTemplate ? currenTemplate : props.currentTemplate
          )
        : cloneDeep(list);
}

// 从数组中随机获取8个数组成新的数组
function getRandomArry(listLength: number) {
    let result = [];
    let arr = Array.from(Array(listLength).keys());

    var count = arr.length;
    for (var i = 0; i < 8; i++) {
        var index = ~~(Math.random() * count) + i;
        result[i] = arr[index];
        arr[index] = arr[i];
        count--;
    }
    return result;
}

function getRandomRecommandList(
    list: Array<ExperienceTemplateV4 | ECTemplate>,
    currentTemplate: ExperienceTemplateV4 | ECTemplate | undefined
) {
    const withoutCurrentTemplateList = list.filter(item => {
        return item?.id !== currentTemplate?.id;
    });
    const filterTemplates = withoutCurrentTemplateList.filter(
        (item: ExperienceTemplateV4 | ECTemplate) => {
            return (
                currentTemplate?.tags?.[props.recommandType as string]?.some((i: string) => {
                    return item.tags?.[props.recommandType as string]?.includes(i);
                }) && item.tags?.type === currentTemplate?.tags?.type
            );
        }
    );

    const len = filterTemplates.length;
    let randomIndexArr = len > 8 ? getRandomArry(len) : [];
    return len <= 8
        ? filterTemplates
        : filterTemplates.filter((item, index) => {
              return randomIndexArr.includes(index);
          });
}

function formatLink(url: string, sid: any) {
    if (!url) return;
    let formatUrl = url;
    if (!sid) {
        formatUrl = url.replace(/\/{sid}/, '');
    } else {
        formatUrl = url.replace(/{sid}/, sid);
    }
    return formatUrl;
}

function handleInfoTemplate(template: ExperienceTemplateV4) {
    const t =
        template && typeof template === 'string'
            ? allECTemplates.value?.find((item: ECTemplate) => item.id === template)
            : template;
    let type = 'template';
    if (t?.isfromEC) {
        type = 'idea';
    }
    if (t?.tags?.layout === 'collection') {
        type = 'collection';
        const link = formatLink(t?.i18nLink1, router.currentRoute.value?.params?.sid);
        if (link) {
            window.open(link);
        }
    }
    if (t?.tags?.layout === 'knowledge') {
        type = 'knowledge';
        const link = formatLink(t?.i18nLink1, router.currentRoute.value?.params?.sid);
        if (link) {
            window.open(link);
        }
    }
    analyticsService
        .usePTX('gallery_item_click', {
            item_type: type,
            item_name: t?.name,
            item_id: t?.id,
            click_details: 'view_details',
            position: props.position,
            is_blank: template?.id === 'blank' ? 'Y' : 'N'
        })
        .track();
}

function handleViewTemplate(template: ExperienceTemplateV4) {
    const t =
        template && typeof template === 'string'
            ? allECTemplates.value?.find((item: ECTemplate) => item.id === template)
            : template;
    useECEvent('trackProductDetailImpression', t, props.position);
    let type = 'template';
    let details = 'details';
    if (t?.isfromEC) {
        type = 'idea';
    }
    if (t?.tags?.layout === 'collection') {
        type = 'collection';
        details = 'check_templates';
        const link = formatLink(t?.i18nLink2, router.currentRoute.value?.params?.sid);
        if (link) {
            window.open(link);
        }
    }
    if (t?.tags?.layout === 'knowledge') {
        type = 'knowledge';
        details = 'check_templates';
    }
    analyticsService
        .usePTX('gallery_item_click', {
            item_type: type,
            item_name: t?.name,
            item_id: t?.id,
            click_details: details,
            position: props.position,
            is_blank: template?.id === 'blank' ? 'Y' : 'N'
        })
        .track();
    if (t?.tags?.layout !== 'collection' && t?.tags?.layout !== 'knowledge') {
        emit('view', t);
        analyticsService
            .usePTX('cp_template_detail_click', {
                type: t?.type,
                position: props.position,
                template_name: t?.name
            })
            .track();
    }
    if (props.currentTemplate && props.recommandType) {
        currentRecommondList.value = explorList(getDefaultRecommandList(t));
        nextTick(() => {
            handleScroll();
        })
    }
}

function explorList(list: any) {
    list.forEach((x: any) => {
        if(x){
            x.isRender = false;
        }
    })
    if(props.position !== 'gallery_all' || account.value?.area === 'EN'){
        list = list.filter((item: ExperienceTemplateV4 | ECTemplate) => {
            return item?.tags?.layout !== 'collection' && item?.tags?.layout !== 'knowledge';
        })
    }
    return list
}

function handleCreateTemplate(template: ExperienceTemplateV4 | string) {
    const t =
        template && typeof template === 'string'
            ? allECTemplates.value?.find((item: ECTemplate) => item.id === template)
            : template;
    emit('create', t);
    useECEvent('trackAddToCart', t as ExperienceTemplateV4, props.position);
    let type = 'template';
    if (t?.isfromEC) {
        type = 'idea';
    }
    analyticsService
        .usePTX('gallery_item_click', {
            item_type: type,
            item_name: t?.name,
            item_id: t?.id,
            click_details: 'create',
            position: props.position,
            is_blank: template?.id === 'blank' ? 'Y' : 'N'
        })
        .track();
    analyticsService
        .usePTX('cp_template_create_click', {
            type: t.type,
            is_blank: template?.id === 'blank' ? 'Y' : 'N',
            template_position: 'template_list',
            template_name: template?.id === 'blank' ? '' : t.name
        })
        .track();
}

function handeEditTemplate(template: ExperienceTemplateV4) {
    emit('edit', template);
}

function handleDeleteTemplate(template: ExperienceTemplateV4) {
    emit('delete', template);
}

function openSuggestDialog() {
    suggest();
}

function handleScroll() {
    let collectionIndex = 0;
    let colorIndex = 0;
    const els = cardRef.value?.querySelectorAll('div.cardBase');
    if (els?.length > 0) {
        els.forEach((el: any, index: number) => {
            const collection = Array.from(el.classList).includes('ecCollection');
            const card = Array.from(el.classList).includes('ecCard');
            if (collection) {
                el && el.setAttribute('data-color', collectionColorList.value[collectionIndex]);
                collectionIndex++;
                if (collectionIndex === collectionColorList.value.length) {
                    collectionIndex = 0;
                }
            }
            if (card) {
                el && el.setAttribute('data-color', JSON.stringify(cardColorList.value[colorIndex]));
                colorIndex++;
                if (colorIndex === cardColorList.value.length) {
                    colorIndex = 0;
                }
            }
            const observer = new IntersectionObserver(
                ([{ isIntersecting }]) => {
                    if (isIntersecting) {
                        const t = currentRecommondList.value[index];
                        if (!t?.isRender) {
                            if (t) {
                                t.isRender = true;
                                let type = 'template';
                                if (t?.isfromEC) {
                                    type = 'idea';
                                }
                                if (t?.tags?.layout === 'collection') {
                                    type = 'collection';
                                }
                                if (t?.tags?.layout === 'knowledge') {
                                    type = 'knowledge';
                                }
                                analyticsService
                                    .usePTX('gallery_item_impression', {
                                        item_type: type,
                                        item_name: t?.name,
                                        item_id: t?.id,
                                        position: props.position,
                                        is_blank: t?.id === 'blank' ? 'Y' : 'N'
                                    })
                                    .track();
                            }
                        }
                        observer.unobserve(el);
                    }
                },
                {
                    threshold: 0.01
                }
            );
            observer.observe(el);
        });
    }
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.recommandList {
    position: relative;
    &.background {
        background-color: $pt-black-10;
        border-top: 1px solid $pt-black-40;
        padding: 32px 45px;
    }
    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .title {
            font-size: 16px;
            color: $pt-black-900;
            margin-bottom: 20px;
            font-weight: 500;
        }
    }
    .listContainer {
        min-height: 480px;
    }
    .list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        grid-template-rows: repeat(auto-fill, 1fr);
        grid-gap: 32px 44px;
        padding: 0px 0px 40px 0;

        .cardBase {
            min-width: 270px;
            min-height: 270px;
        }
    }
    &.large {
        .list {
            grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
            .cardBase {
                min-width: 270px;
                min-height: 270px;
            }
        }
    }
}

.recommandListMobile {
    padding: 10px 20px !important;

    :global {
        .ec-card-container .ec-card .picture .hoverCard {
            display: none !important;
        }
    }
}

.emptyTip {
    width: 100%;
    text-align: center;
    position: absolute;
    top: 0;
    left: 0;
    img {
        width: 112px;
        height: 102px;
        margin: 0 auto;
        margin-top: 60px;
        display: block;
    }
    .title {
        font-size: 16px;
        color: $pt-black-900;
        text-align: center;
        margin: 14px 0 4px;
    }
    .tip {
        font-size: 13px;
        color: $pt-black-90;
        line-height: 20px;
    }
    .suggest {
        text-decoration: underline;
        cursor: pointer;
    }
}
</style>
